'use client';

import {useEffect} from 'react';
import {useRouter} from 'next/navigation';

import {useI18n} from '@udemy-v2/i18n';
import {ToasterStore, type AlertBannerProps} from '@udemy-v2/react-notice-components';

import {removeUrlSearchParam} from '@/lib/remove-url-search-param';

export const CLAIM_RESULT_PARAM = 'retention-promo-claim';

/**
 * Surfaces retention promo claim results via the app-level Toaster.
 * Reads `?retention-promo-claim=` and calls ToasterStore.addAlertBannerToast.
 * Renders nothing — ToasterWrapper in the root layout hosts the toast UI.
 */
export function RetentionPromoClaimAlert() {
    const {gettext} = useI18n();
    const router = useRouter();

    useEffect(() => {
        const result = new URLSearchParams(window.location.search).get(CLAIM_RESULT_PARAM);
        if (!result) {
            return;
        }

        const successTitle = gettext(
            'Success! Your Personal Plan is locked in at your new price for the next year.',
        );
        const successBody = gettext(
            "We'll charge your card on your renewal date, and you can cancel anytime before then. You've made the right call — and momentum's only going to pick up from here!",
        );
        const messages: Record<
            string,
            {title: string; body?: string; udStyle: AlertBannerProps['udStyle']}
        > = {
            succeeded: {title: successTitle, body: successBody, udStyle: 'success'},
            already_claimed: {title: successTitle, body: successBody, udStyle: 'success'},
            expired: {title: gettext('Sorry, this offer has expired.'), udStyle: 'error'},
            failed: {
                title: gettext('Sorry, something went wrong. Please try again later.'),
                udStyle: 'error',
            },
        };
        const message = messages[result];
        if (!message) {
            return;
        }

        const bannerProps: AlertBannerProps = {
            udStyle: message.udStyle,
            title: message.title,
            body: message.body,
            actionButtonProps: false,
            onDismiss: () => removeUrlSearchParam(router, CLAIM_RESULT_PARAM),
        };

        // toastKey prevents duplicate toasts under React Strict Mode remounts.
        ToasterStore.addAlertBannerToast(bannerProps, {
            autoDismiss: false,
            toastKey: `${CLAIM_RESULT_PARAM}-${result}`,
        });
    }, [gettext, router]);

    return null;
}
